import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge } from '../../components/ui/Badge.js';
import { ExpandedRow, ExpField } from '../../components/ui/ExpandedRow.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import type { Usuario } from '../clientes/types.js';
import type { EstadoPermisos, Seguimiento, SeguimientoDetalle } from './types.js';

const COLSPAN = 6;

const ESTADO_PERMISOS_LABEL: Record<EstadoPermisos, string> = {
  no_solicitados: 'No Solicitados',
  solicitados:    'Solicitados',
  autorizados:    'Autorizados',
};

const ESTADO_PERMISOS_TONE: Record<EstadoPermisos, 'neutral' | 'warning' | 'success'> = {
  no_solicitados: 'neutral',
  solicitados:    'warning',
  autorizados:    'success',
};

function EstadoPermisosBadge({ estado }: { estado: EstadoPermisos }) {
  return <Badge tone={ESTADO_PERMISOS_TONE[estado]}>{ESTADO_PERMISOS_LABEL[estado]}</Badge>;
}

function fmtFechaHora(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5M3 21v-5h5" />
    </svg>
  );
}

// Timer de UI puro (sin dependencias de backend) — Prompt: Ajustes finos,
// ítem 1. Se re-renderiza cada segundo solo para refrescar el texto relativo.
function fmtActualizadoHace(dataUpdatedAt: number, ahora: number): string {
  if (!dataUpdatedAt) return '';
  const segundos = Math.max(0, Math.floor((ahora - dataUpdatedAt) / 1000));
  if (segundos < 5) return 'Actualizado justo ahora';
  if (segundos < 60) return `Actualizado hace ${segundos} seg`;
  const minutos = Math.floor(segundos / 60);
  return `Actualizado hace ${minutos} min`;
}

// Mismo patrón que Historial de Clientes: el nombre se muestra siempre (la
// fila de usuarios nunca se borra físicamente), con un indicador gris si el
// usuario ya no está activo (pausado o eliminado).
function QuienYCuando({ nombre, activo, fecha }: { nombre: string; activo: boolean; fecha: string | null }) {
  return (
    <span style={!activo ? { color: 'var(--muted)' } : undefined}>
      {nombre}
      {!activo && <span title="Usuario inactivo o eliminado"> (inactivo)</span>} · {fmtFechaHora(fecha)}
    </span>
  );
}

interface DetalleExpandidoProps {
  seguimiento: Seguimiento;
  correoPorNombre: Record<string, string>;
  puedeCompletar: boolean;
  onCompletar: () => void;
  completando: boolean;
}

// Fetch del detalle (involucrados) solo cuando la fila está expandida — mismo
// dato que antes se cargaba al abrir el modal, ahora vive en su propia fila.
function DetalleExpandido({ seguimiento, correoPorNombre, puedeCompletar, onCompletar, completando }: DetalleExpandidoProps) {
  const { get } = useApi();
  const { data: detalle } = useQuery({
    queryKey: ['seguimientos', 'detalle', seguimiento.id],
    queryFn: () => get<SeguimientoDetalle>(`/seguimientos/${seguimiento.id}`),
  });

  return (
    <ExpandedRow colSpan={COLSPAN}>
      <ExpField label="Cliente">{seguimiento.cliente_nombre}</ExpField>
      <ExpField label="Plataforma">{seguimiento.plataforma_nombre}</ExpField>
      <ExpField label="Involucrados en permisos" gridColumn="3 / 5">
        {!detalle
          ? 'Cargando…'
          : detalle.involucrados.length
            ? detalle.involucrados.map(nombre => (correoPorNombre[nombre] ? `${nombre} (${correoPorNombre[nombre]})` : nombre)).join(', ')
            : '—'}
      </ExpField>
      <ExpField label="Iniciado por">
        <QuienYCuando nombre={seguimiento.iniciado_por_nombre} activo={seguimiento.iniciado_por_activo} fecha={seguimiento.iniciado_en} />
      </ExpField>
      {seguimiento.estado === 'cerrado' && (
        <ExpField label="Cerrado por">
          <QuienYCuando
            nombre={seguimiento.cerrado_por_nombre ?? '—'}
            activo={seguimiento.cerrado_por_activo ?? false}
            fecha={seguimiento.cerrado_en}
          />
        </ExpField>
      )}
      {puedeCompletar && (
        <div style={{ gridColumn: '1 / -1' }}>
          <button className="btn btn-primary btn-sm" disabled={completando} onClick={onCompletar}>
            Marcar como completado
          </button>
        </div>
      )}
    </ExpandedRow>
  );
}

export function SeguimientosPage() {
  const { get, patch } = useApi();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [estadoPermisosFiltro, setEstadoPermisosFiltro] = useState('');
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());

  const { data: seguimientos, isLoading, dataUpdatedAt, refetch, isFetching } = useQuery({
    queryKey: ['seguimientos', estadoPermisosFiltro],
    queryFn: () => get<Seguimiento[]>(`/seguimientos${estadoPermisosFiltro ? `?estadoPermisos=${estadoPermisosFiltro}` : ''}`),
  });

  // Solo para refrescar el texto "Actualizado hace…" en vivo — no dispara
  // ningún fetch, dataUpdatedAt ya lo trae TanStack Query.
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const { data: usuariosData } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => get<Usuario[]>('/usuarios'),
  });
  const correoPorNombre = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of usuariosData ?? []) map[u.nombre] = u.correo;
    return map;
  }, [usuariosData]);

  const completarMutation = useMutation({
    mutationFn: (id: string) => patch(`/seguimientos/${id}/completar`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seguimientos'] });
      showToast('success', 'Seguimiento marcado como completado.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible completar el seguimiento.');
    },
  });

  function puedeCompletar(s: Pick<Seguimiento, 'estado' | 'iniciado_por'>): boolean {
    if (s.estado !== 'abierto') return false;
    return user?.rol === 'root' || user?.correo === s.iniciado_por;
  }

  function toggleExpandida(id: string) {
    setExpandidas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const lista = seguimientos ?? [];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Seguimientos</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>
          Estatus de permisos de plataformas por cliente.
        </div>
      </div>

      <div className="filters-bar">
        <select className="filter-input" value={estadoPermisosFiltro} onChange={e => setEstadoPermisosFiltro(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="no_solicitados">No Solicitados</option>
          <option value="solicitados">Solicitados</option>
          <option value="autorizados">Autorizados</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {dataUpdatedAt > 0 && <span className="refresh-meta">{fmtActualizadoHace(dataUpdatedAt, ahora)}</span>}
          <button
            type="button"
            className="refresh-btn"
            onClick={() => void refetch()}
            disabled={isFetching}
            title="Actualizar seguimientos"
            aria-label="Actualizar seguimientos"
          >
            <span className={isFetching ? 'refresh-spin' : undefined}>
              <RefreshIcon />
            </span>
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Plataforma</th>
                <th>Estado</th>
                <th>Iniciado</th>
                <th>Cerrado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={COLSPAN}>
                    {isLoading ? 'Cargando…' : 'No hay seguimientos que coincidan con el filtro'}
                  </td>
                </tr>
              ) : (
                lista.flatMap(s => {
                  const expandida = expandidas.has(s.id);
                  const filas = [
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.cliente_nombre}</td>
                      <td>{s.plataforma_nombre}</td>
                      <td>
                        <EstadoPermisosBadge estado={s.estado_permisos} />
                      </td>
                      <td>
                        <span style={!s.iniciado_por_activo ? { color: 'var(--muted)' } : undefined}>
                          {s.iniciado_por_nombre}
                          {!s.iniciado_por_activo && ' (inactivo)'} · {fmtFechaHora(s.iniciado_en)}
                        </span>
                      </td>
                      <td>
                        {s.estado === 'cerrado' ? (
                          <span style={s.cerrado_por_activo === false ? { color: 'var(--muted)' } : undefined}>
                            {s.cerrado_por_nombre}
                            {s.cerrado_por_activo === false && ' (inactivo)'} · {fmtFechaHora(s.cerrado_en)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <button className="td-expand-btn" onClick={() => toggleExpandida(s.id)}>
                          {expandida ? '▲ Cerrar' : '▼ Detalle'}
                        </button>
                      </td>
                    </tr>,
                  ];

                  if (expandida) {
                    filas.push(
                      <DetalleExpandido
                        key={`${s.id}-exp`}
                        seguimiento={s}
                        correoPorNombre={correoPorNombre}
                        puedeCompletar={puedeCompletar(s)}
                        completando={completarMutation.isPending}
                        onCompletar={() => completarMutation.mutate(s.id)}
                      />,
                    );
                  }

                  return filas;
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="table-footer">
          <span className="table-count">
            {lista.length} seguimiento{lista.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
